import { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserStatus,
  getShopVariantLimit,
  getAccountAndBillingDetails,
  getStoreDetails,
  getAuthorizeInventoryTransfersRedirectLink,
} from "./services/index";
import { userActions } from "./store/user-slice";
import RouterConfig from "./navigation/RouterConfig";
import AppBridgeNavMenu from "./components/layout/AppBridgeNavMenu";
import NoActiveSubscriptionModal from "./components/billing/NoActiveSubscriptionModal";
import useNoActiveSubscription from "./hooks/useNoActiveSubscription";
import AppWindowHost from "./components/layout/AppWindowHost";
import AppWindowPageHeading from "./components/layout/AppWindowPageHeading";
import PreserveAppWindowQuery from "./components/layout/PreserveAppWindowQuery";
import { useHistory } from "react-router-dom";

// import {} from "react-redux";
// import { useSelector } from "react-redux";
// import Notification from "./components/UI/notification/Notification";

import "./App.css";

import { ROOT, ERROR, ONBOARDING_BILLING_PLAN } from "./navigation/CONSTANTS";

import { authActions } from "./store/auth-slice";
import { reviewActions } from "./store/review-slice";
import { identifyUserForAmplitude, initAmplitude } from "./utils/thirdparty/amplitude";
import { initInspectlet } from "./utils/thirdparty/inspectlet";
import { initIntercom } from "./utils/thirdparty/intercom";
import { getShouldRedirectUserToEmailRecipientsPageFlag, clearShouldRedirectUserToEmailRecipientsPageFlag, getDesiredSettingsGotoTab, clearDesiredSettingsGotoTab, setPostAuthReturnUrl, isInventoryTransferScopesPromptSnoozed, setInventoryTransferScopesPromptSnoozedAt } from "./utils/localStorageUtils";
import { authenticateEmbeddedSessionAction } from "./store/actions/auth-actions";
import { redirectToShopifyUrl, redirectToShopifyInstall } from "./utils/embeddedAppUtils";
import { isInAppWindow } from "./utils/appWindowUtils";
import useReviewFlow from "./hooks/useReviewFlow";
import NormalizationMessage from "./components/UI/review/NormalizationMessage";
import InAppReviewPrompt from "./components/UI/review/InAppReviewPrompt";
import { PROFILE } from "./navigation/CONSTANTS";
import { ACCOUNT_SETTINGS_GOTO } from "./config/CONSTANTS";
import {
  isAuthRedirectDisabled,
  logSkippedAuthRedirect,
} from "./utils/authRedirectUtils";
import { normalizeAccountRecord } from "./utils/accountPayloadUtils";

function App() {
  const history = useHistory();
  // const notification = useSelector((state) => state.ui.notification);
  const dispatch = useDispatch();
  const { isLoggedIn } = useSelector((state) => state.auth);
  // Computed once here and shared with NoActiveSubscriptionModal (to show the
  // blocking overlay) and RouterConfig (to skip mounting data-heavy pages
  // like Forecasting while a cancelled shop has no active plan).
  const noActiveSubscription = useNoActiveSubscription();
  const { userDetails } = useSelector((state) => state.user);
  const { isOnboardingCompleted } = useSelector((state) => state.jobstatus);
  const { shouldShowNormalization } = useReviewFlow();

  /*
   * Boots the embedded app: verifies the current Shopify App Bridge session token
   * against the backend and routes to wherever the merchant left off in onboarding.
   * This replaces the old flow where the backend's post-OAuth redirect drove
   * authentication via a one-time token; now the frontend authenticates itself on
   * every load using the session token App Bridge hands it.
   */
  useEffect(() => {
    dispatch(authenticateEmbeddedSessionAction(history));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    getUserStatus()
      .then((res) => {
        const response = res.data;
        if (response.installation_status !== true) {
          if (isAuthRedirectDisabled()) {
            logSkippedAuthRedirect(
              "getUserStatus installation redirect",
              response.redirect_url
            );
            checkStoreVarientsCount();
          } else {
            // Permanent token is gone (uninstall/reinstall). Go through /install so
            // a fresh OAuth nonce is persisted -- the inline authorize URL from
            // getDefaultAppInstallUrl does not save the nonce, so postauth would 401.
            redirectToShopifyInstall();
          }
        } else {
          checkStoreVarientsCount();
        }
      })
      .catch((err) => {
        console.log("axios err=", err);
      });

    return () => {};
  }, [isLoggedIn]);

  const checkStoreVarientsCount = async () => {
    const res = await getShopVariantLimit();

    if (res.success === true) {
      const { allowed, variantCount, maxVariantLimit } = res.data;

      if (!allowed) {
        dispatch(
          authActions.setStoreLimitStatus({
            status: true,
            count: variantCount,
            maxVariantLimit,
          })
        );
        history.push(`${ERROR}?status=1`);
      } else {
        checkStoreRevenue();
        dispatch(
          authActions.setStoreLimitStatus({
            status: false,
            count: variantCount,
            maxVariantLimit,
          })
        );
      }
    }
  };

  const checkStoreRevenue = async () => {
    try {

      const res = await getStoreDetails();

      const storeData = res.data[0];
      // If response is successful and revenue limit is exceeded, redirect to billing plan page to prompt user to upgrade
      if (res.success && storeData.isExceededFreePlanRevenueLimit === true) {
        // Introduced a short delay to ensure all API calls and state updates on the Forecasting page complete smoothly.
        // This helps avoid premature redirects or rendering issues since it's the landing page after login.
        console.log("Redirecting in 2 seconds...");
        setTimeout(() => {
          history.push(`${ONBOARDING_BILLING_PLAN}?status=1`);
        }, 2000);
      } else if (!res.success) {
        console.log("Error Occured Please Try again");
      }
    } catch (err) {
      console.log("axios err=", err);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    getAccountAndBillingDetails()
      .then((res) => {
        const account = normalizeAccountRecord(res.userDetails);
        dispatch(
          userActions.setDetails({
            userDetails: account,
          })
        );
        dispatch(userActions.setChatbotAccessFromAccount(account));
        if (res.billingPlanDetails?.data) {
          dispatch(
            userActions.setBillingDetails({
              billingDetails: res.billingPlanDetails.data,
            })
          );
        }
        if (!isInAppWindow()) {
          // App Window loads a second SPA; skip duplicate third-party boot.
          initInspectlet(account.shop_name)

          const billingPlan = res.billingPlanDetails?.data?.id ?? "";
          initIntercom(account, billingPlan);
          const user = account;
          identifyUserForAmplitude(user.shop_name, {
            email: user.admin_email,
            role: user.role
          });
        }
      })
      .catch((err) => {
        if (!isInAppWindow()) {
          initIntercom("");
        }
        console.log("axios err=", err);
      });

    // Fetch currency code once when user logs in
    if (isLoggedIn) {
      getStoreDetails()
        .then((res) => {
          if (res.success && res.data && res.data[0] && res.data[0].currency_code) {
            dispatch(
              userActions.setCurrencyCode({
                currencyCode: res.data[0].currency_code,
              })
            );
          }
        })
        .catch((err) => {
          console.log("Error fetching store details for currency code:", err);
        });
    }

    return () => {};
  }, [isLoggedIn]);

  useEffect(() => {
    if (isInAppWindow()) return;
    initAmplitude();
  }, []);

  useEffect(() => {
    if (!isInAppWindow()) {
      return undefined;
    }
    document.body.classList.add("in-app-window");
    return () => {
      document.body.classList.remove("in-app-window");
    };
  }, []);

  // Handle email recipients page redirect flag when app is opened
  useEffect(() => {
    if (isLoggedIn) {
      const shouldRedirectToEmailRecipients = getShouldRedirectUserToEmailRecipientsPageFlag();
      if (shouldRedirectToEmailRecipients) {
        clearShouldRedirectUserToEmailRecipientsPageFlag();
        const goto = getDesiredSettingsGotoTab() || ACCOUNT_SETTINGS_GOTO; // default to account settings (goto=1)
        clearDesiredSettingsGotoTab();
        // Add a small delay to ensure the app is fully loaded
        setTimeout(() => {
          history.push(`${PROFILE}?goto=${goto}`);
        }, 1000);
      }
    }
  }, [isLoggedIn, history]);

  // On login, check if the user has granted inventory transfer API scopes.
  // If not, redirect them to the Shopify grant-permissions page.
  // A 1-week cooldown prevents repeated prompts so the user isn't asked on every login.
  useEffect(() => {
    if (!userDetails) return;
    if (userDetails.hasInventoryTransferApiScopes === true) return;
    if (isInventoryTransferScopesPromptSnoozed()) return;

    setInventoryTransferScopesPromptSnoozedAt();
    setPostAuthReturnUrl(ROOT);

    getAuthorizeInventoryTransfersRedirectLink(userDetails.shop_name)
      .then((res) => {
        if (isAuthRedirectDisabled()) {
          logSkippedAuthRedirect(
            "inventory transfer scopes redirect",
            res.data
          );
          return;
        }
        redirectToShopifyUrl(res.data);
      })
      .catch((err) => {
        console.log("Error fetching inventory transfer scopes redirect URL:", err);
      });
  }, [userDetails]);

  // In-App Review Flow: show the one-time "You are all set now" normalization card
  // once both userDetails are loaded and onboarding is reported as completed.
  // Decision is gated by useReviewFlow.shouldShowNormalization which also checks
  // localStorage for prior dismissals and the "already reviewed" flag.
  useEffect(() => {
    if (userDetails && isOnboardingCompleted && shouldShowNormalization()) {
      dispatch(reviewActions.showNormalization());
    }
  }, [userDetails, isOnboardingCompleted, shouldShowNormalization, dispatch]);

  return (
    <Fragment>
      {/* {notification && (
        <Notification
          status={notification.status}
          title={notification.title}
          message={notification.message}
        />
      )} */}

      <NormalizationMessage />
      <InAppReviewPrompt />

      <AppWindowHost />
      <AppWindowPageHeading />
      <PreserveAppWindowQuery />
      <AppBridgeNavMenu />
      <NoActiveSubscriptionModal isOpen={noActiveSubscription} />
      <RouterConfig noActiveSubscription={noActiveSubscription} />
    </Fragment>
  );
}

export default App;
